import React, { useState, useEffect } from "react";
import { BrandAmbassadorRewardlevels } from "../../constants/brandAmbassador";
import { SingularPlural } from "../../utils/wordUtils";
import { apiFetchAmbassadorLatestPerformance } from "../../api/backend/brandAmbassador";
import "./ambassadorRewardStatusRateCard.scss";

// Utility functions
const MAX_POINTS = BrandAmbassadorRewardlevels.reduce((max, lvl) => {
  const [, upper] = lvl.pointsRange;
  return upper === Infinity ? max : Math.max(max, upper);
}, 0);

function clampToPercentage(points) {
  if (points <= 0) return 0;
  if (points >= MAX_POINTS) return 100;
  return (points / MAX_POINTS) * 100;
}

function getLeftPercent(levelStart) {
  if (levelStart === Infinity) return 100;
  if (levelStart <= 0) return 0;
  if (levelStart >= MAX_POINTS) return 100;
  return (levelStart / MAX_POINTS) * 100;
}

/** Pick current + next level for a given signup count. */
function pickLevels(signups, levels) {
  const idx = levels.findIndex(
    ({ pointsRange: [min, max] }) =>
      signups >= min && (max === Infinity || signups <= max)
  );

  const currentIndex = idx === -1 ? levels.length - 1 : idx;
  const nextIndex = currentIndex + 1 < levels.length ? currentIndex + 1 : null;

  return {
    currentLevel: levels[currentIndex],
    nextLevel: nextIndex !== null ? levels[nextIndex] : null,
  };
}

export default function AmbassadorRewardStatusRateCard({
  currentUserId,
  role,
}) {
  const [cumulativeSignups, setCumulativeSignups] = useState(0);
  const [loading, setLoading] = useState(true);
  const [progressPercent, setProgressPercent] = useState(0);
  const [rewardLevelMessage, setRewardLevelMessage] = useState("");
  const [currentRate, setCurrentRate] = useState(1.0);

  useEffect(() => {
    if (role !== "brandAmbassador") {
      setLoading(false);
      return;
    }
    apiFetchAmbassadorLatestPerformance(currentUserId)
      .then((res) => {
        if (res.data.status === "OK" && res.data.data) {
          setCumulativeSignups(res.data.data.cumulativeSignups || 0);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentUserId, role]);

  // Recompute UI bits when signups change
  useEffect(() => {
    setProgressPercent(clampToPercentage(cumulativeSignups));

    const { currentLevel, nextLevel } = pickLevels(
      cumulativeSignups,
      BrandAmbassadorRewardlevels
    );
    setCurrentRate(currentLevel.rate);

    if (nextLevel) {
      const remaining = Math.max(
        0,
        nextLevel.pointsRange[0] - cumulativeSignups
      );
      setRewardLevelMessage(
        `Only ${remaining} ${SingularPlural(
          remaining,
          "signUpForm"
        )} until you reach ${nextLevel.name} level!`
      );
    } else {
      setRewardLevelMessage(
        `You’ve reached the highest level: ${currentLevel.name}!`
      );
    }
  }, [cumulativeSignups]);

  if (role !== "brandAmbassador")
    return <p>No brand ambassador record found.</p>;
  if (loading) return <p>Loading your reward progress…</p>;

  return (
    <div className="ambassador-status-container">
      <h3 className="ambassadorRewardStatusTitle">
        Daily Cumulative Signup Reward Progress
      </h3>

      <div className="ambassador-progress-bar">
        <div className="ambassador-progress-line">
          <div
            className="ambassador-progress-fill"
            style={{ width: `${progressPercent}%` }}
          />
          <div
            className="ambassador-pointer"
            style={{ left: `${progressPercent}%` }}
          >
            <img
              src="/rewardindicator.svg"
              className="statusIndicator"
              alt="pointer"
            />
          </div>
        </div>

        <div className="ambassador-milestones-labeled">
          {BrandAmbassadorRewardlevels.map((level, idx) => {
            const [min, max] = level.pointsRange;
            const left = getLeftPercent(min);
            const label = max === Infinity ? `${min}+` : `${min}`;
            return (
              <div
                key={idx}
                className="milestone-labeled"
                style={{ left: `${left}%` }}
              >
                <img
                  src={level.icon}
                  alt={level.name}
                  className="milestone-icon"
                />
                <span className="milestone-label">{label}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="ambassador-message-container">
        <p className="ambassador-message">{rewardLevelMessage}</p>
        <p className="ambassador-rate">
          Your Current Rate: ${currentRate.toFixed(2)}
        </p>
      </div>
    </div>
  );
}
